import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const T = {
  title: { de: 'Cookie-Einstellungen', en: 'Cookie Settings' },
  desc: {
    de: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Einige Cookies sind für den Betrieb der Website unerlässlich, während andere uns helfen, die Website und Ihre Erfahrung zu verbessern.',
    en: 'We use cookies to provide you with the best possible experience on our website. Some cookies are essential for the operation of the website, while others help us improve the website and your experience.',
  },
  essential: { de: 'Essentielle Cookies', en: 'Essential Cookies' },
  essentialDesc: {
    de: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    en: 'These cookies are required for the basic functions of the website and cannot be disabled.',
  },
  analytics: { de: 'Analyse-Cookies', en: 'Analytics Cookies' },
  analyticsDesc: {
    de: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.',
    en: 'These cookies help us understand how visitors interact with our website.',
  },
  showMore: { de: 'Mehr erfahren', en: 'Learn more' },
  showLess: { de: 'Weniger anzeigen', en: 'Show less' },
  essentialOnly: { de: 'Nur Essentielle', en: 'Essential Only' },
  acceptAll: { de: 'Alle akzeptieren', en: 'Accept All' },
};

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      essential: true, analytics: true, marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const acceptEssential = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      essential: true, analytics: false, marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="container-main">
          <div className="bg-[#152238] border border-[#1e3a5f] shadow-2xl p-6 md:p-8 rounded-xl">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0F1A2E] flex items-center justify-center flex-shrink-0">
                  <Cookie size={20} className="text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white">{T.title[language]}</h3>
              </div>
              <button onClick={acceptEssential} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="cookie-close-btn">
                <X size={20} />
              </button>
            </div>

            <p className="text-white/70 mb-4 leading-relaxed">{T.desc[language]}</p>

            {showDetails && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mb-6 space-y-4 border-t border-gray-100 pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 mt-1 bg-[#0F1A2E]"></div>
                  <div>
                    <p className="font-semibold text-white">{T.essential[language]}</p>
                    <p className="text-sm text-white/50">{T.essentialDesc[language]}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 mt-1 bg-[#F5A623]"></div>
                  <div>
                    <p className="font-semibold text-white">{T.analytics[language]}</p>
                    <p className="text-sm text-white/50">{T.analyticsDesc[language]}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-[#0F1A2E] text-sm font-medium hover:text-[#F5A623] transition-colors underline underline-offset-2"
                data-testid="cookie-details-btn"
              >
                {showDetails ? T.showLess[language] : T.showMore[language]}
              </button>
              <div className="flex gap-3">
                <button onClick={acceptEssential} className="px-6 py-3 text-sm font-semibold uppercase tracking-wider border border-white/30 text-white hover:bg-white/10 transition-colors" data-testid="cookie-essential-btn">
                  {T.essentialOnly[language]}
                </button>
                <button onClick={acceptAll} className="px-6 py-3 text-sm font-semibold uppercase tracking-wider bg-[#F5A623] text-[#0F1A2E] hover:bg-white transition-colors" data-testid="cookie-accept-all-btn">
                  {T.acceptAll[language]}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
